import bindAll from 'lodash.bindall';
import PropTypes from 'prop-types';
import React from 'react';
import VM from 'openblock-vm';
import {connect} from 'react-redux';
import {compose} from 'redux';
import {defineMessages, FormattedMessage, injectIntl, intlShape} from 'react-intl';

import analytics from '../lib/analytics';
import {setDeviceData} from '../reducers/device-data';
import {openRobotManager, openKitManager} from '../reducers/modals';
import {setDeviceType} from '../reducers/device';
import {setCodeEditorValue} from '../reducers/code';

import {makeDeviceLibrary} from '../lib/libraries/devices/index.jsx';
import {getRobotMockCode} from '../lib/mstem-mock/robot-codegen';

import LibraryComponent from '../components/library/library.jsx';
import libraryStyles from '../components/library/library.css';
import deviceIcon from '../components/action-menu/icon--sprite.svg';

const messages = defineMessages({
    deviceTitle: {
        defaultMessage: 'Choose an Device',
        description: 'Heading for the device library',
        id: 'gui.deviceLibrary.chooseADevice'
    },
    deviceUrl: {
        defaultMessage: 'Enter the URL of the device',
        description: 'Prompt for unoffical device url',
        id: 'gui.deviceLibrary.deviceUrl'
    },
    arduinoTag: {
        defaultMessage: 'Arduino',
        description: 'Arduino tag to filter all arduino devices.',
        id: 'gui.deviceLibrary.arduinoTag'
    },
    microPythonTag: {
        defaultMessage: 'MicroPython',
        description: 'Micro python tag to filter all micro python devices.',
        id: 'gui.deviceLibrary.microPythonTag'
    },
    kitTag: {
        defaultMessage: 'Kit',
        description: 'Kit tag to filter all kit devices.',
        id: 'gui.deviceLibrary.kitTag'
    },
    tabRobot: {
        defaultMessage: '🤖 Robot',
        description: 'Robot tab in device library (Phase 2 robots-kits mode)',
        id: 'gui.deviceLibrary.tabRobot'
    },
    tabSmartHomeKit: {
        defaultMessage: '🏠 SmartHome Kit',
        description: 'SmartHome kit tab in device library (Phase 2 robots-kits mode)',
        id: 'gui.deviceLibrary.tabSmartHomeKit'
    },
    manageRobots: {
        defaultMessage: 'Quản lý Robot',
        description: 'Button to open Robot CRUD manager from device library footer',
        id: 'gen.deviceLibrary.manageRobots'
    },
    manageKits: {
        defaultMessage: 'Quản lý Kit',
        description: 'Button to open Kit CRUD manager from device library footer',
        id: 'gen.deviceLibrary.manageKits'
    }
});

// MSTEM-NOTE Phase 2 Pitfall #6 — Phase 4 (ROB-05) will introduce a board-picker
// using LibraryComponent in mode='boards' with these 3 tags. The constants stay
// defined even though Phase 2 device-library now invokes mode='robots-kits'.
// eslint-disable-next-line no-unused-vars
const ARDUINO_TAG = {tag: 'Arduino', intlLabel: messages.arduinoTag};
// eslint-disable-next-line no-unused-vars
const MICROPYTHON_TAG = {tag: 'MicroPython', intlLabel: messages.microPythonTag};
// eslint-disable-next-line no-unused-vars
const KIT_TAG = {tag: 'Kit', intlLabel: messages.kitTag};
// eslint-disable-next-line no-unused-vars
const tagListPrefix = [ARDUINO_TAG, MICROPYTHON_TAG, KIT_TAG];

// MSTEM-ADDED Phase 2 GEN-03 — tabsConfig for robots-kits mode (DG-02).
// Heuristic filters for prototype: Arduino + MicroPython tagged items map to
// the Robot tab, Kit tagged items map to the SmartHome Kit tab. BE team will
// replace this with real category metadata once the device taxonomy lands.
const ROBOT_TAB_TAGS = ['arduino', 'micropython'];
const KIT_TAB_TAGS = ['kit'];
const matchesAnyTag = (item, tagList) => {
    if (!Array.isArray(item.tags)) return false;
    const lower = item.tags.map(t => String(t).toLowerCase());
    return tagList.some(t => lower.indexOf(t) !== -1);
};

class DeviceLibrary extends React.PureComponent {
    constructor (props) {
        super(props);
        bindAll(this, [
            'handleClickManage',
            'handleClickManageKits',
            'handleItemSelect',
            'requestLoadDevice'
        ]);
        this.tabsConfig = [
            {
                key: 'robot',
                label: <FormattedMessage {...messages.tabRobot} />,
                filter: item => matchesAnyTag(item, ROBOT_TAB_TAGS)
            },
            {
                key: 'kit',
                label: <FormattedMessage {...messages.tabSmartHomeKit} />,
                filter: item => matchesAnyTag(item, KIT_TAB_TAGS)
            }
        ];
    }
    componentDidMount () {
        this.props.vm.extensionManager.getDeviceList().then(data => {
            this.props.onSetDeviceData(makeDeviceLibrary(data));
        })
            .catch(() => {
                this.props.onSetDeviceData(makeDeviceLibrary());
            });
    }
    handleClickManage () {
        this.props.onRequestClose();
        this.props.onOpenRobotManager();
    }
    handleClickManageKits () {
        this.props.onRequestClose();
        this.props.onOpenKitManager();
    }

    requestLoadDevice (device) {
        const id = device.deviceId;
        const deviceExtensions = device.deviceExtensions;

        // MSTEM-ADDED Phase 4 ROB-05 (Pitfall 1 Option A) — "MBF Robot v1" is a
        // mock device. We DELIBERATELY do NOT call the real device-load path
        // nor leave realtime mode: doing so would flip the editor into Arduino
        // upload mode, and runtime.getBlocksXML hides scratch-extension
        // categories when deviceId !== null && !isRealtimeMode, making the
        // 🤖 Robot category vanish. Instead we just load the builtin extension
        // (blocks live in _blockInfo, always visible in realtime mode), set
        // deviceType arduino so Monaco shows cpp, and inject the symbolic
        // Arduino stub (DR-08). Stays in realtime mode → category persists.
        if (device.deviceId === 'mbfRobotV1') {
            if (!this.props.vm.extensionManager.isExtensionLoaded('mbfRobot')) {
                this.props.vm.extensionManager.loadExtensionURL('mbfRobot');
            }
            this.props.onDeviceSelected('mbfRobotV1', device.name, 'arduino');
            this.props.onSetDeviceType('arduino');
            this.props.onSetMockCode(getRobotMockCode());
            return;
        }

        if (id && !device.disabled) {
            if (this.props.vm.extensionManager.isDeviceLoaded(id)) {
                this.props.onDeviceSelected(id);
            } else {
                this.props.vm.extensionManager.loadDeviceURL(device).then(() => {
                    this.props.vm.extensionManager.getDeviceExtensionsList().then(() => {
                        // TODO: Add a event for install device extension
                        // the large extensions will take many times to load
                        // A loading interface should be launched.
                        this.props.vm.installDeviceExtensions(Object.assign([], deviceExtensions));
                    });
                    this.props.onDeviceSelected(id);
                    analytics.event({
                        category: 'devices',
                        action: 'select device',
                        label: id
                    });
                })
                    .catch(err =>
                        console.error(err) // eslint-disable-line no-console
                    );
            }
        }
    }

    handleItemSelect (item) {
        this.requestLoadDevice(item);
        this.props.onRequestClose();
    }

    render () {
        const deviceLibraryThumbnailData = this.props.deviceData.map(device => ({
            rawURL: device.iconURL || deviceIcon,
            ...device
        }));

        // MSTEM-ADDED Phase 2 GEN-03 / extended GEN-04 — Manage button slot
        // is now tab-aware: Robot tab → navy "Quản lý Robot",
        // Kit tab → cyan "Quản lý Kit". Both buttons use CSS Modules
        // (no inline style — per CONVENTIONS.md + W-2 fix).
        const manageRobotButton = (
            <button
                className={libraryStyles.manageBtn}
                type="button"
                onClick={this.handleClickManage}
            >
                {this.props.intl.formatMessage(messages.manageRobots)}
            </button>
        );

        const manageKitButton = (
            <button
                className={libraryStyles.manageBtnCyan}
                type="button"
                onClick={this.handleClickManageKits}
            >
                {this.props.intl.formatMessage(messages.manageKits)}
            </button>
        );

        const manageButtonByTab = {
            robot: manageRobotButton,
            kit: manageKitButton
        };

        return (
            <LibraryComponent
                data={deviceLibraryThumbnailData}
                defaultTabKey="robot"
                id="deviceLibrary"
                manageButtonByTab={manageButtonByTab}
                mode="robots-kits"
                tabsConfig={this.tabsConfig}
                title={this.props.intl.formatMessage(messages.deviceTitle)}
                onItemSelected={this.handleItemSelect}
                onRequestClose={this.props.onRequestClose}
            />
        );
    }
}

DeviceLibrary.propTypes = {
    deviceData: PropTypes.instanceOf(Array).isRequired,
    intl: intlShape.isRequired,
    onDeviceSelected: PropTypes.func,
    onOpenKitManager: PropTypes.func.isRequired,
    onOpenRobotManager: PropTypes.func.isRequired,
    onRequestClose: PropTypes.func,
    onSetDeviceData: PropTypes.func.isRequired,
    onSetDeviceType: PropTypes.func.isRequired,
    onSetMockCode: PropTypes.func.isRequired,
    vm: PropTypes.instanceOf(VM).isRequired // eslint-disable-line react/no-unused-prop-types
};

const mapStateToProps = state => ({
    deviceData: state.scratchGui.deviceData.deviceData
});

const mapDispatchToProps = dispatch => ({
    onSetDeviceData: data => dispatch(setDeviceData(data)),
    onOpenRobotManager: () => dispatch(openRobotManager()),
    onOpenKitManager: () => dispatch(openKitManager()),
    onSetDeviceType: type => dispatch(setDeviceType(type)),
    onSetMockCode: value => dispatch(setCodeEditorValue(value))
});

export default compose(
    injectIntl,
    connect(
        mapStateToProps,
        mapDispatchToProps
    )
)(DeviceLibrary);
